import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import './ShipstationOrder.css';

const ShipstationOrder = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(order => 
        order.orderId?.toString().includes(searchTerm) ||
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.billTo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.orderStatus?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://gokulnair.com/new_project/api/shipstation/orders');
      
      if (response.data.status && response.data.data) {
        setOrders(response.data.data.orders || []);
        setFilteredOrders(response.data.data.orders || []);
      } else {
        setError('Invalid response format from API');
      }
    } catch (err) {
      setError('Failed to fetch shipstation orders');
      console.error('Error fetching shipstation orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'shipped':
        return 'status-shipped';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  };

  const getPaymentMethodClass = (method) => {
    switch (method?.toLowerCase()) {
      case 'stripe':
        return 'payment-stripe';
      case 'paypal':
        return 'payment-paypal';
      case 'offline':
        return 'payment-offline';
      default:
        return 'payment-unknown';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading shipstation orders...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="shipstation-content">
        {error && <div className="error-message">{error}</div>}
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <h3>Total Orders</h3>
              <p className="stat-number">{orders.length.toLocaleString()}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🚚</div>
            <div className="stat-content">
              <h3>Shipped Orders</h3>
              <p className="stat-number">
                {orders.filter(order => order.orderStatus?.toLowerCase() === 'shipped').length.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>Total Revenue</h3>
              <p className="stat-number">
                {formatCurrency(orders.reduce((sum, order) => sum + (order.amountPaid || 0), 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="search-section">
          <div className="search-container">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Search orders by order ID, order number, email, customer name, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="search-results">
            {searchTerm && (
              <p>Found {filteredOrders.length} order(s) matching "{searchTerm}"</p>
            )}
          </div>
        </div>

        <div className="shipstation-orders">
          <h2>Shipstation Orders</h2>
          <div className="orders-table">
            {filteredOrders.length === 0 ? (
              <div className="no-orders">
                {searchTerm ? `No orders found matching "${searchTerm}"` : 'No orders found'}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Payment Method</th>
                    <th>Order Date</th>
                    <th>Shipping</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.orderId}>
                      <td>
                        <button 
                          className="order-id-link"
                          onClick={() => navigate(`/shipstation-order/${order.orderId}`)}
                        >
                          {order.orderId}
                        </button>
                      </td>
                      <td>
                        <span className="order-number">
                          {order.orderNumber}
                        </span>
                      </td>
                      <td>
                        <div className="customer-info">
                          <div className="customer-name">{order.billTo?.name || 'N/A'}</div>
                          <div className="customer-phone">{order.billTo?.phone || 'N/A'}</div>
                        </div>
                      </td>
                      <td>{order.customerEmail || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(order.orderStatus)}`}>
                          {order.orderStatus || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        <div className="items-info">
                          {order.items?.map((item, index) => (
                            <div key={index} className="item-detail">
                              <span className="item-name">{item.name}</span>
                              <span className="item-quantity">x{item.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="price-info">
                          <div className="total-amount">{formatCurrency(order.orderTotal)}</div>
                          <div className="paid-amount">Paid: {formatCurrency(order.amountPaid)}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`payment-badge ${getPaymentMethodClass(order.paymentMethod)}`}>
                          {order.paymentMethod || 'N/A'}
                        </span>
                      </td>
                      <td>{formatDate(order.orderDate)}</td>
                      <td>
                        <div className="shipping-info">
                          <div className="shipping-service">{order.requestedShippingService || 'N/A'}</div>
                          <div className="shipping-cost">{formatCurrency(order.shippingAmount)}</div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShipstationOrder; 